import React, { useState, useMemo } from 'react';
import TransactionsStatsCards from './TransactionsStatsCards';
import VolumeDetails from './VolumeDetails';

const TransactionsTable = ({ transactions, sellOrders = [], buyOrders = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'time', direction: 'descending' });

  // Handle null or undefined transactions, sellOrders, and buyOrders by providing default empty arrays
  const safeTransactions = transactions || [];
  const safeSellOrders = sellOrders || [];
  const safeBuyOrders = buyOrders || [];

  // Process transactions to add warnings
  const processedTransactions = useMemo(() => safeTransactions.map(transaction => {
    let warning = false;
    let warningNote = '';
    let warningVariation = 0;

    // Select the relevant orders based on the transaction type
    const relevantOrders = transaction.orderType === 'Sell' ? safeSellOrders : safeBuyOrders;
    const filteredOrders = relevantOrders.filter(order => {
      return (new Date(order.updatedAt) < new Date(transaction.time)) &&
        (transaction.orderType === 'Sell' ? order.price < transaction.price * 0.97 : order.price > transaction.price * 1.03);
    });

    if (filteredOrders.length > 0) {
      warning = filteredOrders.length > 0;
      warningVariation = Math.abs(transaction.price - filteredOrders[0].price) / transaction.price * 100;
      warningNote = `Transaction executed with a ${warningVariation.toFixed(2)}% ${transaction.orderType === 'Sell' ? 'higher' : 'lower'} price than the best current order`;
    }

    if (!warning) {
      if (transaction.orderType === 'Buy') {
        // Check if there is a buy transaction with price greater than an older sell transaction price
        const filteredSellOrders = safeSellOrders.filter(order => new Date(order.updatedAt) < new Date(transaction.time) && order.price < transaction.price);
        if (filteredSellOrders.length > 0) {
          warning = true;
          warningVariation = Math.abs(transaction.price - filteredSellOrders[0].price) / transaction.price * 100;
          warningNote = `Transaction executed with a ${warningVariation.toFixed(2)}% higher price than the best current sell order`;
        }
      } else if (transaction.orderType === 'Sell') {
        // Check if there is a sell transaction with price lower than an older buy transaction price
        const filteredBuyOrders = safeBuyOrders.filter(order => new Date(order.updatedAt) < new Date(transaction.time) && order.price > transaction.price);
        if (filteredBuyOrders.length > 0) {
          warning = true;
          warningVariation = Math.abs(transaction.price - filteredBuyOrders[0].price) / transaction.price * 100
          warningNote = `Transaction executed with a ${warningVariation.toFixed(2)}% lower price than the best current sell order`;
        }
      }
    }

    return {
      ...transaction,
      amount: parseFloat(transaction.amount),
      price: parseFloat(transaction.price),
      value: parseFloat(transaction.price) * parseFloat(transaction.amount),
      warning,
      warningNote
    };
  }), [safeTransactions, safeSellOrders, safeBuyOrders]);

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...processedTransactions];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return showAll ? sortableItems : sortableItems.slice(0, 10);
  }, [processedTransactions, sortConfig, showAll]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="overflow-x-auto relative sm:rounded-lg mb-8">
      <h3 className="text-xl font-bold">Last Transactions</h3>
      <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
        <VolumeDetails transactions={transactions} />
        <TransactionsStatsCards transactions={transactions} />
      </div>
      <table className="w-full text-sm text-center text-gray-600">
        <thead className="text-xs uppercase bg-gray-50">
          <tr>
            <th onClick={() => requestSort('warning')} className="py-2 px-4">
              ⚠️ {sortConfig.key === 'warning' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
            <th onClick={() => requestSort('orderType')} className="py-2 px-4 cursor-pointer">
              Type {sortConfig.key === 'orderType' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
            <th onClick={() => requestSort('amount')} className="py-2 px-4 cursor-pointer text-right">
              Amount {sortConfig.key === 'amount' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
            <th onClick={() => requestSort('price')} className="py-2 px-4 cursor-pointer text-right">
              Price {sortConfig.key === 'price' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
            <th onClick={() => requestSort('value')} className="py-2 px-4 cursor-pointer text-right">
              Value {sortConfig.key === 'value' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
            <th onClick={() => requestSort('time')} className="py-2 px-4 cursor-pointer">
              Time {sortConfig.key === 'time' ? (sortConfig.direction === 'descending' ? '↑' : '↓') : ''}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions.map((transaction, index) => {
            let backgroundColor = '#f3f4f6';  // Default background color for all rows
            if (index < sortedTransactions.length - 1 && sortConfig.key === "time" && sortConfig.direction === 'descending') {
              const nextTransaction = sortedTransactions[index + 1];
              backgroundColor = transaction.warning
                ? 'rgba(255, 239, 153, 0.3)'
                : transaction.price === nextTransaction.price
                  ? backgroundColor
                  : transaction.price > nextTransaction.price
                    ? 'rgba(194, 245, 225, 0.3)'
                    : 'rgba(238, 195, 195, 0.3)';
            }
            return (
              <tr key={transaction.time + transaction.amount + transaction.price}
                className="border-b"
                style={{ backgroundColor: backgroundColor }}>
                <td className="py-2 px-4">
                  {transaction.warning &&
                    <div className="group cursor-pointer relative inline-block border-gray-400 text-center">⚠️
                      <div className="w-72 opacity-0 bg-gray-800 text-white text-center text-xs rounded-lg py-2 absolute z-10 group-hover:opacity-100 bottom-full px-3 pointer-events-none">
                        {transaction.warningNote}
                      </div>
                    </div>
                  }
                </td>
                <td className="py-2 px-4 font-medium text-gray-700 whitespace-nowrap">
                  {transaction.orderType}
                </td>
                <td className="py-2 px-4 text-right">
                  {"Ƚ" + transaction.amount.toLocaleString('en-US', {
                    maximumFractionDigits: 0
                  })}
                </td>
                <td className="py-2 px-4 text-right">
                  ${transaction.price.toFixed(5)}
                </td>
                <td className="py-2 px-4 text-right">
                  ${(transaction.value).toLocaleString('en-US', {
                    maximumFractionDigits: 0
                  })}
                </td>
                <td className="py-2 px-4">
                  {new Date(transaction.time).toLocaleString()}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="text-center my-4">
        <button onClick={() => setShowAll(!showAll)} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          {showAll ? 'Show Less' : 'Show More'}
        </button>
      </div>
    </div>
  );
};

export default TransactionsTable;
